// Playoff bracket: pure renderer — champion banner, round columns, matchup
// cards, and round-1 bye cards. No fetching; PlayoffsPage owns the data.
import React from 'react';
import { Link } from 'react-router-dom';
import { TrophyIcon } from '@heroicons/react/24/outline';
import { PlayoffBracket, PlayoffSeed, PlayoffMatchup, Team } from '../../types';

interface Props {
  leagueId: number;
  bracket: PlayoffBracket;
  seeds: PlayoffSeed[];
  rounds: PlayoffMatchup[][];
}

function roundName(round: number, totalRounds: number): string {
  const fromEnd = totalRounds - round;
  if (fromEnd === 0) return 'Championship';
  if (fromEnd === 1) return 'Semifinals';
  if (fromEnd === 2) return 'Quarterfinals';
  return `Round ${round}`;
}

function SeedChip({ seed }: { seed: number | undefined }) {
  if (seed == null) return null;
  return (
    <span
      aria-label={`Seed ${seed}`}
      className="w-6 h-6 shrink-0 flex items-center justify-center bg-ink-900 text-gold font-display text-xs leading-none"
    >
      <span aria-hidden="true">{seed}</span>
    </span>
  );
}

function TeamRow({
  team,
  seed,
  score,
  showScore,
  isWinner,
}: {
  team: Team | null;
  seed: number | undefined;
  score: number | null;
  showScore: boolean;
  isWinner: boolean;
}) {
  return (
    <div
      data-testid={isWinner ? 'winner-row' : undefined}
      className={`flex items-center gap-2 px-3 py-2 ${isWinner ? 'font-bold border-l-2 border-gold' : ''}`}
    >
      <SeedChip seed={seed} />
      {team ? (
        <span className="flex-1 min-w-0 truncate text-sm text-ink-900">{team.team_name}</span>
      ) : (
        <span className="flex-1 min-w-0 truncate text-sm text-secondary-400 italic">TBD</span>
      )}
      {isWinner && <span className="sr-only">(winner)</span>}
      {showScore && (
        <span className="shrink-0 text-sm text-ink-900 tabular-nums">{score ?? 0}</span>
      )}
    </div>
  );
}

function MatchupCard({
  leagueId,
  matchup,
  seedOf,
}: {
  leagueId: number;
  matchup: PlayoffMatchup;
  seedOf: Map<number, number>;
}) {
  const showScore = matchup.status === 'completed';
  const name1 = matchup.team1?.team_name ?? 'TBD';
  const name2 = matchup.team2?.team_name ?? 'TBD';
  return (
    <Link
      to={`/app/leagues/${leagueId}/matchups/${matchup.id}`}
      aria-label={`View matchup: ${name1} vs ${name2}`}
      className="block card divide-y divide-secondary-200 hover:border-ink-900 transition-colors"
    >
      <TeamRow
        team={matchup.team1}
        seed={matchup.team1 ? seedOf.get(matchup.team1.id) : undefined}
        score={matchup.team1_score}
        showScore={showScore}
        isWinner={showScore && matchup.winner_team_id === matchup.team1_id}
      />
      <TeamRow
        team={matchup.team2}
        seed={matchup.team2 ? seedOf.get(matchup.team2.id) : undefined}
        score={matchup.team2_score}
        showScore={showScore}
        isWinner={showScore && matchup.winner_team_id === matchup.team2_id}
      />
    </Link>
  );
}

function ByeCard({ seed, team }: { seed: number; team: Team | null }) {
  return (
    <div className="card flex items-center gap-2 px-3 py-2">
      <SeedChip seed={seed} />
      <span className="flex-1 min-w-0 truncate text-sm text-ink-900">{team?.team_name ?? 'TBD'}</span>
      <span className="shrink-0 px-1.5 py-0.5 text-[10px] font-display uppercase bg-paper text-ink-900 border border-ink-900">
        BYE
      </span>
    </div>
  );
}

function ChampionBanner({ bracket }: { bracket: PlayoffBracket }) {
  return (
    <div className="bg-ink-900 text-paper px-6 py-8 flex flex-col items-center text-center gap-2">
      <TrophyIcon className="w-10 h-10 text-gold" />
      <p className="font-display uppercase tracking-wide text-sm text-secondary-300">LEAGUE CHAMPION</p>
      <p className="font-display uppercase text-3xl text-gold">{bracket.champion?.team_name ?? 'TBD'}</p>
      {bracket.runner_up && (
        <p className="font-serif text-sm text-secondary-300">Runner-up: {bracket.runner_up.team_name}</p>
      )}
    </div>
  );
}

const BracketView: React.FC<Props> = ({ leagueId, bracket, seeds, rounds }) => {
  const seedOf = new Map<number, number>(seeds.map((s) => [s.team_id, s.seed]));
  const teamOfSeed = new Map<number, Team | null>(seeds.map((s) => [s.seed, s.team]));

  return (
    <div>
      {bracket.status === 'completed' && (
        <div className="mb-6">
          <ChampionBanner bracket={bracket} />
        </div>
      )}
      <div className="overflow-x-auto">
        <div className="flex gap-6 min-w-max pb-2">
          {rounds.map((matchups, i) => {
            const round = i + 1;
            return (
              <div key={round} className="w-56 shrink-0 space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wider text-secondary-500">
                  {roundName(round, bracket.total_rounds)}
                </p>
                <div className="space-y-3">
                  {round === 1 &&
                    bracket.byes.map((seed) => (
                      <ByeCard key={`bye-${seed}`} seed={seed} team={teamOfSeed.get(seed) ?? null} />
                    ))}
                  {matchups.map((m) => (
                    <MatchupCard key={m.id} leagueId={leagueId} matchup={m} seedOf={seedOf} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default BracketView;
